import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { SystemSettingsResponse } from "@multi-chat/shared";
import { isApiError } from "../../api/client.js";
import { getSupportContact } from "../../api/organizations-api.js";
import { ProductErrorState } from "../../components/common/ProductErrorState.js";
import {
  currentSuspendedOrganizationId,
  subscribeOrganizationSuspended,
} from "./organization-suspended-signal.js";

export function isOrganizationSuspendedError(error: unknown): boolean {
  return isApiError(error) && error.code === "ORGANIZATION_SUSPENDED";
}

export function customerFacingOrganizationFailure(
  translate: (key: string) => string,
  error: unknown,
): { title: string; body: string } {
  if (error === undefined || isOrganizationSuspendedError(error)) {
    return {
      title: translate("organization.suspended.title"),
      body: translate("organization.suspended.body"),
    };
  }
  return {
    title: translate("organization.genericError.title"),
    body: translate("organization.genericError.body"),
  };
}

export function useSuspendedOrganizationId(): number | null {
  const [organizationId, setOrganizationId] = useState(currentSuspendedOrganizationId);
  useEffect(() => subscribeOrganizationSuspended(setOrganizationId), []);
  return organizationId;
}

export function OrganizationSupportContacts({
  support,
}: {
  support: SystemSettingsResponse;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const lines = [
    support.supportEmail
      ? { label: t("organization.suspended.supportEmail"), href: `mailto:${support.supportEmail}`, value: support.supportEmail }
      : null,
    support.supportPhone
      ? { label: t("organization.suspended.supportPhone"), href: `tel:${support.supportPhone}`, value: support.supportPhone }
      : null,
    support.supportWhatsApp
      ? {
          label: t("organization.suspended.supportWhatsApp"),
          href: `https://wa.me/${support.supportWhatsApp.replace(/\D/g, "")}`,
          value: support.supportWhatsApp,
        }
      : null,
    support.supportUrl
      ? { label: t("organization.suspended.supportUrl"), href: support.supportUrl, value: support.supportUrl }
      : null,
  ].filter((line) => line !== null);
  if (lines.length === 0) return null;
  return (
    <div className="mx-auto mt-4 w-full max-w-md text-start" data-testid="organization-support-contact">
      <p className="m-0 text-[0.8125rem] font-medium text-ink">{t("organization.suspended.supportTitle")}</p>
      <ul className="m-0 mt-2 list-none space-y-1 p-0">
        {lines.map((line) => (
          <li key={line.label} className="text-[0.8125rem] leading-5 text-muted">
            <span>{line.label}</span>
            {": "}
            <a className="text-accent underline-offset-2 hover:underline" href={line.href} rel="noreferrer">
              <bdi>{line.value}</bdi>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function OrganizationUnavailableState({
  error,
}: {
  error?: unknown;
}): React.JSX.Element {
  const { t } = useTranslation();
  const copy = customerFacingOrganizationFailure(t, error);
  const [support, setSupport] = useState<SystemSettingsResponse | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    void getSupportContact(controller.signal)
      .then((value) => {
        if (!controller.signal.aborted) setSupport(value);
      })
      .catch(() => {
        if (!controller.signal.aborted) setSupport(null);
      });
    return () => controller.abort();
  }, []);
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-4 py-8">
      <ProductErrorState
        title={copy.title}
        description={copy.body}
        testId="organization-unavailable"
      />
      {support ? <OrganizationSupportContacts support={support} /> : null}
    </div>
  );
}
